import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { auth, tokens, type UserInfo } from '@/api/client'
import { useNavigate } from 'react-router-dom'

export function useAuth() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const meQuery = useQuery<UserInfo | null>({
    queryKey: ['me'],
    queryFn: async () => {
      if (!tokens.access) return null
      try {
        return await auth.me()
      } catch {
        tokens.clear()
        return null
      }
    },
    staleTime: 60_000,
  })

  const loginMutation = useMutation({
    mutationFn: async (vars: { email: string; password: string; twoFactorCode?: string }) => {
      const res = await auth.login(vars.email, vars.password, vars.twoFactorCode)
      if ('requiresTwoFactor' in res) return res
      tokens.set(res.accessToken, res.refreshToken)
      return res
    },
    onSuccess: (data) => {
      if ('user' in data) {
        queryClient.setQueryData(['me'], data.user)
        navigate('/projects')
      }
    },
  })

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const refreshToken = tokens.refresh
      if (refreshToken) {
        try { await auth.logout(refreshToken) } catch { /* ignora */ }
      }
      tokens.clear()
      queryClient.setQueryData(['me'], null)
    },
    onSuccess: () => {
      queryClient.clear()
      navigate('/login')
    },
  })

  return {
    user: meQuery.data,
    isAuthenticated: !!meQuery.data,
    isLoading: meQuery.isLoading,
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
  }
}
